/**
 * /help — every help video for the viewer's role, grouped like their menu. A
 * client sees client videos and a coach coach videos; a section with no video
 * is left out.
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CirclePlay } from "lucide-react";

import { HelpVideoCard } from "~/components/learning/help-video";
import { Card } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { PageSection } from "~/components/ui/page-section";
import { PageHeader } from "~/components/ui/page-title";
import { Skeleton } from "~/components/ui/skeleton";
import { helpVideosQueryOptions } from "~/lib/queries/learningQuery";
import { groupHelpVideos } from "~/lib/shared/learning/help-topics";

export const Route = createFileRoute("/_user/help")({
  staticData: { title: "Help" },
  loader: async ({ context }) => {
    await context.queryClient
      .ensureQueryData(helpVideosQueryOptions())
      .catch(() => undefined);
  },
  component: HelpPage,
});

function HelpPage() {
  const { data, isPending, isError } = useQuery(helpVideosQueryOptions());
  const groups = data ? groupHelpVideos(data.role, data.videos) : [];

  return (
    <div className="space-y-8">
      <PageHeader
        title="Help"
        description="Short videos that show how each page works."
      />
      {isPending ? (
        <Skeleton aria-label="Loading videos" className="h-48 rounded-2xl" />
      ) : isError ? (
        <Card>
          <EmptyState
            icon={CirclePlay}
            title="Videos did not load"
            description="Reload the page to try again."
          />
        </Card>
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={CirclePlay}
            title="No videos yet"
            description="We are adding short videos for your pages. Check back soon."
          />
        </Card>
      ) : (
        groups.map((group) => (
          <PageSection key={group.section.id} title={group.section.label}>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {group.listings.map((listing) => (
                <HelpVideoCard key={listing.key} listing={listing} />
              ))}
            </div>
          </PageSection>
        ))
      )}
    </div>
  );
}
